const GOVERNED_SOURCE_DIGEST_ID = 'approved_source_systems' as const;
void GOVERNED_SOURCE_DIGEST_ID;
import { DECISION_AGENT_SOURCE_CLASSES } from './contracts/sourceClasses';
import {
  SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION,
  SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS,
  SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION,
  SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION,
} from './scenario-investment-evidence-artifacts';
import {
  SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS,
  SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS,
} from './contracts/segmentMarginCleanupModuleEvidenceContract';

export const APPROVED_SOURCE_SYSTEMS_SCHEMA_VERSION = 'approved_source_systems_registry_v1';

export type ApprovedSourceSystemRuntimeUse = 'governed_evidence_only';

export type ApprovedSourceSystemDefinition = Readonly<{
  sourceSystemRef: string;
  label: string;
  sourceClass: string;
  allowedSourceClasses: readonly string[];
  allowedContextKinds?: readonly string[];
  evidenceSchemaVersion: string;
  /**
   * Additional accepted evidence schema versions for the same source system
   * (e.g. a `_v2` snapshot that adds a member field). Validation accepts the
   * union of `evidenceSchemaVersion` and these; it never removes a version.
   */
  allowedEvidenceSchemaVersions?: readonly string[];
  runtimeUse: ApprovedSourceSystemRuntimeUse;
  description: string;
}>;

export const STRATEGIC_EXECUTION_CAPACITY_CSV_SOURCE_SYSTEM_REF =
  'ask_helix_memory:strategic_execution_capacity_evidence_v1';

export const ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:org_analysis_optimization_governed_snapshot_v1';

export const ACTIVE_HEADCOUNT_IN_SCOPE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:active_headcount_in_scope_governed_snapshot_v1';

export const CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:carrier_transportation_cost_governed_snapshot_v1';

export const FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF =
  'ask_helix_memory:finance_pnl_public_filing_v1';

export const FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF =
  'ask_helix_memory:finance_pnl_manual_admin_v1';

export const CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF =
  'ask_helix_memory:capital_cash_balance_sheet_snapshot_v1';

export const INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF =
  'ask_helix_memory:industry_benchmark_reference_ranges_v1';

export const PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:pricing_realization_governed_snapshot_v1';

export const REALIZATION_MONITORING_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:realization_monitoring_governed_snapshot_v1';

export const RISK_CLAIMS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:risk_claims_governed_snapshot_v1';
export const PORTFOLIO_CAPACITY_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:portfolio_capacity_allocation_governed_snapshot_v1';
export const COST_TO_SERVE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:cost_to_serve_governed_snapshot_v1';
export const CAPITAL_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:capital_allocation_governed_snapshot_v1';
export const COMMERCIAL_EFFECTIVENESS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:commercial_effectiveness_governed_snapshot_v1';

export const PRICING_DISCOUNT_POLICY_AUTHORITY_SOURCE_SYSTEM_REF =
  'ask_helix_memory:pricing_discount_policy_authority_matrix_v1';

export const PRICING_DISCOUNT_TRANSACTION_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:pricing_discount_transaction_snapshot_v1';

export const PRODUCTIVITY_LEAN_AI_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:productivity_lean_ai_governed_snapshot_v1';

export const WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF =
  'ask_helix_memory:workforce_reduction_governed_evidence_pack_v1';

export const REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF =
  'ask_helix_memory:realization_component_variance_v1';

export const ATTRIBUTION_COUNTERFACTUAL_EVIDENCE_SOURCE_SYSTEM_REF =
  'ask_helix_memory:attribution_counterfactual_evidence_v1';

export const ATTRIBUTION_DECONFLICTION_RECEIPT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:attribution_deconfliction_receipt_v1';

export const ALLOCATION_LENS_EXCLUSION_RECEIPT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:allocation_lens_exclusion_receipt_v1';

export const ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF =
  'ask_helix_memory:allocation_lens_member_universe_v1';

export const ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND =
  'decision_agent:allocation_lens_member_universe';

export const ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION =
  'allocation_lens_member_universe_v1';

export const SEGMENT_MARGIN_CLEANUP_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:segment_margin_cleanup_governed_snapshot_v1';

// Modeled margin-fact decomposition remains cent-reconciled to approved segment P&L anchors.
export const SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:segment_margin_cleanup_margin_fact_v1';

// Founder-authorized MODELED gl_account operating-cost decomposition (labeled
// modeled/illustrative; reconciled to governed anchors; csv-admin-upload lifecycle).
export const MODELED_GL_OPERATING_COST_SOURCE_SYSTEM_REF =
  'ask_helix_memory:modeled_gl_operating_cost_v1';

export const TRAVEL_EXPENSE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:travel_expense_governed_snapshot_v1';

export const INDIRECT_PROCUREMENT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:indirect_procurement_governed_snapshot_v1';

export const PROFESSIONAL_FEES_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:professional_fees_governed_snapshot_v1';

export const TECH_SOFTWARE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:tech_software_governed_snapshot_v1';

export const REAL_ESTATE_FOOTPRINT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:real_estate_footprint_governed_snapshot_v1';

export const SHARED_SERVICES_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:shared_services_governed_snapshot_v1';

export const MARKETING_SPEND_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:marketing_spend_governed_snapshot_v1';

export const NETWORK_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:network_optimization_governed_snapshot_v1';

export const CAPACITY_UTILIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:capacity_utilization_governed_snapshot_v1';

export const MODE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:mode_mix_shift_governed_snapshot_v1';

export const FUEL_EFFICIENCY_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:fuel_efficiency_governed_snapshot_v1';

export const OUTSIDE_CONTRACTORS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:outside_contractors_governed_snapshot_v1';

export const LABOR_ARBITRAGE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:labor_arbitrage_governed_snapshot_v1';

export const REVENUE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:revenue_mix_shift_governed_snapshot_v1';

export const GROWTH_CROSS_SELL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:growth_cross_sell_governed_snapshot_v1';

export const CHURN_REDUCTION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:churn_reduction_governed_snapshot_v1';

export const WORKING_CAPITAL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:working_capital_governed_snapshot_v1';

export const CAPEX_DISCIPLINE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:capex_discipline_governed_snapshot_v1';

export const SCENARIO_INVESTMENT_GOVERNED_ARTIFACTS_SOURCE_SYSTEM_REF =
  'ask_helix_memory:scenario_investment_governed_artifacts_v1';

export const ESG_EMISSIONS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF =
  'ask_helix_memory:esg_emissions_governed_snapshot_v1';

const strategicExecutionCapacityEvidenceSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: STRATEGIC_EXECUTION_CAPACITY_CSV_SOURCE_SYSTEM_REF,
  label: 'Strategic execution capacity evidence',
  sourceClass: 'csv-admin-upload',
  allowedSourceClasses: Object.freeze([
    'csv-admin-upload',
    'connector-derived',
    'manual-admin',
    'modeled',
  ]),
  evidenceSchemaVersion: 'strategic_execution_capacity_evidence_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved evidence artifacts for strategic execution capacity diagnostics after sanctioned ingest, manual-admin upload, connector-derived projection, and tenant source-system activation.',
});

const orgCostOptimizationGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Org cost optimization governed snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
  ]),
  evidenceSchemaVersion: 'org_analysis_optimization_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved org-analysis snapshots carrying org state, span/layer metrics, verified position cost, people tenure, severance policy, capacity bindings, and engine workflow references.',
});

const carrierTransportationCostGovernedSnapshotSource: ApprovedSourceSystemDefinition =
  Object.freeze({
    sourceSystemRef: CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    label: 'Carrier transportation cost governed snapshot',
    sourceClass: 'connector-derived',
    allowedSourceClasses: Object.freeze([
      'connector-derived',
      'manual-admin',
      'csv-admin-upload',
      'modeled',
    ]),
    evidenceSchemaVersion: 'carrier_transportation_cost_governed_snapshot_v1',
  allowedEvidenceSchemaVersions: Object.freeze(['carrier_transportation_cost_governed_snapshot_v1', 'carrier_transportation_cost_governed_snapshot_v2']),
    runtimeUse: 'governed_evidence_only',
    description:
      'Approved carrier transportation cost snapshots carrying shipment lane spend, carrier rate-card, contract-window, margin-floor, and lane economics evidence after sanctioned ingest and approval.',
  });

const portfolioCapacityAllocationGovernedSnapshotSource: ApprovedSourceSystemDefinition =
  Object.freeze({
    sourceSystemRef: PORTFOLIO_CAPACITY_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    label: 'Portfolio capacity allocation governed snapshot',
    sourceClass: 'connector-derived',
    // Standing Law 15 (2026-08-04): 'modeled' admits as its own class and keeps
    // that class end to end so the "Modeled" disclosure label rides the answer.
    // The set stays closed; unlisted classes remain rejected.
    allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin', 'modeled']),
    allowedContextKinds: Object.freeze([
      'decision_agent:portfolio_capacity_allocation:portfolio_plan_snapshot',
      'decision_agent:portfolio_capacity_allocation:capacity_actuals_snapshot',
      'decision_agent:portfolio_capacity_allocation:dependency_constraint_snapshot',
      'decision_agent:portfolio_capacity_allocation:allocation_policy',
      'decision_agent:portfolio_capacity_allocation:finance_actuals_snapshot',
    ]),
    evidenceSchemaVersion: 'portfolio_capacity_allocation_governed_snapshot_v1',
    runtimeUse: 'governed_evidence_only',
    description:
      'Approved portfolio initiative, delivery-capacity, dependency, allocation-policy, and finance artifacts admitted for formula-governed capacity allocation decisions.',
  });

const financePnlPublicFilingSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
  label: 'Finance P&L public filing governed snapshot',
  sourceClass: 'public-filing',
  allowedSourceClasses: Object.freeze(['public-filing']),
  evidenceSchemaVersion: 'finance_pnl_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved finance artifacts carrying company P&L, segment, balance-sheet, cash, debt, capex, and guidance values sourced from public filings through sanctioned review and approval.',
});

const financePnlManualAdminSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
  label: 'Finance P&L manual-admin governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze(['manual-admin']),
  evidenceSchemaVersion: 'finance_pnl_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved finance artifacts carrying company P&L, segment, balance-sheet, cash, debt, capex, and guidance values loaded by an authorized admin through sanctioned review and approval.',
});

const capitalCashBalanceSheetPublicFilingSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF,
  label: 'Capital & Cash balance-sheet public filing snapshot',
  sourceClass: 'public-filing',
  allowedSourceClasses: Object.freeze(['public-filing']),
  evidenceSchemaVersion: 'capital_cash_balance_sheet_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved capital-cash balance-sheet snapshots carrying working-capital, cash, capex, and source filing provenance after sanctioned public-filing review and approval.',
});

const industryBenchmarksPublicSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF,
  label: 'Public industry benchmark reference ranges',
  sourceClass: 'industry_reference',
  allowedSourceClasses: Object.freeze(['industry_reference']),
  evidenceSchemaVersion: 'industry_metric_reference_ranges_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved public industry benchmark artifacts carrying per-metric reference ranges, peer observations, citations, and named gaps. Client-pooled benchmark data is not approved for this source system.',
});

const pricingRealizationGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Pricing realization governed snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'pricing_realization_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved pricing realization artifacts carrying sell-rate action, quote-to-invoice leakage, spot/contract structure mix, spot-to-contract intervention exchange, retention, elasticity, execution-cost, and flat-revenue eligibility evidence after sanctioned ingest and approval.',
});

const riskClaimsGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: RISK_CLAIMS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Risk & claims governed snapshot',
  sourceClass: 'extrapolated',
  allowedSourceClasses: Object.freeze([
    'extrapolated',
    'public',
    'synthetic-demo',
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    DECISION_AGENT_SOURCE_CLASSES.modeled,
  ]),
  evidenceSchemaVersion: 'risk_claims_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved cargo-claims register snapshots carrying paid/incurred claims, case + IBNR reserves, open exposure, subrogation recovery, claim count, exposure, and freight-revenue denominator by period, carrier, lane, and cause after sanctioned ingest and approval.',
});

const costToServeGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: COST_TO_SERVE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Cost-to-serve governed snapshot',
  sourceClass: 'modeled',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'cost_to_serve_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved cost-to-serve artifacts carrying account revenue, activity allocation, service-tier cost, elasticity, chart-of-accounts mapping, finance tie-out, and modeled pre-contract demo provenance after sanctioned ingest and approval.',
});

const activeHeadcountInScopeGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ACTIVE_HEADCOUNT_IN_SCOPE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Active headcount (in-scope workforce) governed snapshot',
  sourceClass: 'modeled',
  allowedSourceClasses: Object.freeze([
    'modeled',
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
  ]),
  allowedContextKinds: Object.freeze([
    'ask_helix_memory:active_headcount_in_scope_governed_snapshot',
  ]),
  evidenceSchemaVersion: 'active_headcount_in_scope_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved active-headcount (in-scope workforce) series for the governed workforce-reduction commitment scope: an OrgPositionCost org-substrate anchor (real costed positions by cost centre) plus the modeled committed-curve baseline/target and modeled weekly actuals-to-date carried verbatim from the approved RIF realization commitment. Org-substrate-derived plus modeled fill, labeled, after sanctioned ingest and approval.',
});

const capitalAllocationGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: CAPITAL_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Capital-allocation governed snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'modeled',
  ]),
  evidenceSchemaVersion: 'capital_allocation_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved project-capex pipeline, project cashflow, governed WACC hurdle, budget envelope, and delegated capital-cash control-total artifacts after sanctioned ingest and approval.',
});

const commercialEffectivenessGovernedSnapshotSource: ApprovedSourceSystemDefinition =
  Object.freeze({
    sourceSystemRef: COMMERCIAL_EFFECTIVENESS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    label: 'Commercial effectiveness governed snapshot',
    sourceClass: 'connector-derived',
    allowedSourceClasses: Object.freeze([
      'connector-derived',
      'manual-admin',
      'csv-admin-upload',
      'modeled',
    ]),
    allowedContextKinds: Object.freeze([
      'decision_agent:commercial_effectiveness:pipeline_snapshot',
      'decision_agent:commercial_effectiveness:sales_cost_roster',
      'decision_agent:commercial_effectiveness:churn_retention',
      'decision_agent:commercial_effectiveness:account_attach',
    ]),
    evidenceSchemaVersion: 'commercial_effectiveness_evidence_prep_bundle_v1',
    runtimeUse: 'governed_evidence_only',
    description:
      'Approved commercial-effectiveness evidence carrying pipeline stage history, ramp-adjusted sales-cost roster, retention bridge, account service attach, source-as-of, and finance-reconciliation metadata after sanctioned ingest and approval.',
  });

const pricingDiscountPolicyAuthoritySource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: PRICING_DISCOUNT_POLICY_AUTHORITY_SOURCE_SYSTEM_REF,
  label: 'Pricing discount policy authority matrix',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'pricing_discount_policy_authority_matrix_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved pricing discount authority-band artifacts carrying sanctioned discount thresholds, approver roles, and exception ceilings for discount leakage diagnostics.',
});

const pricingDiscountTransactionSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: PRICING_DISCOUNT_TRANSACTION_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Pricing discount transaction snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'pricing_discount_transaction_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved pricing discount transaction artifacts carrying list revenue, net revenue, actual discount, authorized discount, and off-policy leakage rows for formula-governed diagnostics.',
});

const productivityLeanAiGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: PRODUCTIVITY_LEAN_AI_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Productivity / Lean-AI realization governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'productivity_lean_ai_governed_snapshot_v1',
  allowedEvidenceSchemaVersions: Object.freeze(['productivity_lean_ai_governed_snapshot_v1', 'productivity_lean_ai_governed_snapshot_v2']),
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved productivity realization artifacts carrying throughput-per-FTE baselines, automation-eligible operating cost, realized AI investment, and cost-release evidence after sanctioned ingest and approval.',
});

const workforceReductionGovernedEvidencePackSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF,
  label: 'Workforce reduction governed evidence pack',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin']),
  evidenceSchemaVersion: 'decision_agent_evidence_pack_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved workforce reduction evidence packs carrying governed engine output, org state, position-cost roster, people tenure, severance policy, initiative-capacity binding, and workforce legal gate evidence for Ask Helix scenario answers.',
});

const realizationMonitoringGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: REALIZATION_MONITORING_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Realization monitoring governed snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  allowedContextKinds: Object.freeze(['decision_ledger:realization_commitment']),
  evidenceSchemaVersion: 'realization_pace_receipt_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved realization-monitoring receipts carrying commitment identity, governed actual observation windows, expected-versus-actual position, pace, landing, and source proof for approved commitments.',
});

const realizationComponentVarianceSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF,
  label: 'Realization component variance governed artifact',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin', 'csv-admin-upload']),
  allowedContextKinds: Object.freeze(['decision_agent:realization:component_variance']),
  evidenceSchemaVersion: 'realization_component_variance_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved realization component-variance artifacts keyed by commitment id, carrying component planned-vs-actual cells and provenance for variance_decomposition_v1 drift attribution.',
});

const attributionCounterfactualEvidenceSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ATTRIBUTION_COUNTERFACTUAL_EVIDENCE_SOURCE_SYSTEM_REF,
  label: 'Counterfactual attribution governed evidence',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin']),
  allowedContextKinds: Object.freeze(['decision_ledger:attribution_counterfactual']),
  evidenceSchemaVersion: 'attribution_counterfactual_evidence_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Governed commitment, actual, and pinned do-nothing projection evidence used by counterfactual attribution.',
});

const attributionDeconflictionReceiptSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ATTRIBUTION_DECONFLICTION_RECEIPT_SOURCE_SYSTEM_REF,
  label: 'Attribution de-confliction receipt',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived']),
  allowedContextKinds: Object.freeze(['decision_ledger:attribution_rollup']),
  evidenceSchemaVersion: 'attribution_deconfliction_receipt_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Immutable overlap-resolution receipt binding ImpactClaims, included rows, unresolved pairs, and evidence to a never-sum attribution rollup.',
});

const allocationLensExclusionReceiptSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ALLOCATION_LENS_EXCLUSION_RECEIPT_SOURCE_SYSTEM_REF,
  label: 'Allocation-lens zero-overlap exclusion receipt',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived']),
  allowedContextKinds: Object.freeze([
    'decision_agent:allocation_lens_exclusion_receipt',
  ]),
  evidenceSchemaVersion: 'allocation_lens_exclusion_receipt_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved pair-specific zero-overlap receipts binding exact claims, reporting cuts, common-lineage member-universe digests, and dual-control lifecycle evidence.',
});

const allocationLensMemberUniverseSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
  label: 'Allocation-lens member universe',
  sourceClass: 'modeled',
  allowedSourceClasses: Object.freeze(['modeled', 'connector-derived']),
  allowedContextKinds: Object.freeze([
    ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
  ]),
  evidenceSchemaVersion: ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved immutable member-universe artifacts used to derive allocation-lens ' +
    'exclusion receipts. Each artifact binds one lever family to tenant, scope, ' +
    'reporting cut, source as-of, canonical content digest, member ids, and member digest.',
});

const segmentMarginCleanupGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: SEGMENT_MARGIN_CLEANUP_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Segment margin cleanup governed snapshot',
  sourceClass: 'manual-admin',
  // Standing Law 15 (2026-08-04): 'modeled' admits as its own class and keeps
  // that class end to end so the "Modeled" disclosure label rides the answer.
  // The set stays closed; unlisted classes remain rejected.
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'segment_margin_cleanup_governed_snapshot_v1',
  allowedEvidenceSchemaVersions: Object.freeze(['segment_margin_cleanup_governed_snapshot_v1', 'segment_margin_cleanup_governed_snapshot_v2']),
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved segment margin artifacts carrying segment net revenue, operating margin, margin-floor policy, and cleanup-eligibility evidence after sanctioned ingest and approval.',
});

// #2863 margin-fact evidence class (NEW pin; the governed-snapshot pin above is
// untouched). 'modeled' mirrors the pricing-realization / cost-to-serve pattern:
// the pre-contract labeled modeled decomposition admits as
// governed evidence and KEEPS its modeled sourceClass so the mandatory "Modeled"
// disclosure label survives to the answer surface instead of masquerading as
// connector data. The future certified path is the WSM order-line
// connector-derived extension; admitting it here requires its own reviewed
// registry change — it is deliberately NOT pre-admitted.
const segmentMarginCleanupMarginFactSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF,
  label: 'Segment margin cleanup margin-fact decomposition (modeled)',
  sourceClass: 'modeled',
  allowedSourceClasses: Object.freeze(['modeled']),
  allowedContextKinds: Object.freeze([
    SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.marginFactDecomposition,
  ]),
  // IMPORTED, NEVER RETYPED (#4754 amendment 18, finding 2 / Law 5). The
  // module evidence contract is the SOLE definition of this schema version;
  // the loader that WRITES it and this registry that ADMITS on it both read
  // the one constant, so a rename can never leave them disagreeing about
  // which artifacts exist.
  evidenceSchemaVersion: SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS.marginFactV1,
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved MODELED customer x SKU(nullable) x channel x segment margin-fact rows, ' +
    'labeled modeled at every layer and reconciled to the approved segment margin ' +
    'snapshot (per-segment net revenue, margin, and cleanup opportunity to the cent) ' +
    'after sanctioned ingest and approval. Never certified, never re-labeled, never ' +
    'summed with connector-derived claims. Future certified path: WSM order-line ' +
    '(connector-derived) ingest replacing the modeled decomposition.',
});

const travelExpenseGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: TRAVEL_EXPENSE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Travel & expense governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'travel_expense_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved travel-and-expense artifacts carrying total T&E spend, policy-exception spend, approved leakage and recovery factors, and program cost after sanctioned ingest and approval.',
});

const indirectProcurementGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: INDIRECT_PROCUREMENT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Indirect procurement governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'indirect_procurement_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved non-transport indirect spend artifacts carrying total indirect spend, addressable spend, approved savings rate, recovery factor, and program cost after sanctioned ingest and approval.',
});

const professionalFeesGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: PROFESSIONAL_FEES_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Professional fees governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'professional_fees_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description: 'Approved external legal, consulting, audit, and advisory fee artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval.',
});

const techSoftwareGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: TECH_SOFTWARE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Technology and software governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin', 'csv-admin-upload', 'modeled']),
  evidenceSchemaVersion: 'tech_software_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description: 'Approved SaaS, license, and infrastructure spend artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval.',
});

const realEstateFootprintGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: REAL_ESTATE_FOOTPRINT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Real estate and facilities governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'real_estate_footprint_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description: 'Approved real estate and facility footprint artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval.',
});

// CTO ruling 2026-08-18 (#4602 SHSVC-A), Standing Law 15: 'modeled' joins this
// module's closed admissible set and is CARRIED end to end, never re-labeled, so
// the server-authored "modeled ~" provenance badge rides the answer instead of
// the row being rejected. Ops proved the pre-ruling posture live: the only
// dimensional detail the demo tenant holds is an explicitly pre-contract modeled
// fill, and the HONEST shape for it (a modeled row carrying modeled dimensional
// content) was STRUCTURALLY IMPOSSIBLE — no source system mapped 'modeled' for
// shared-services, so `source_class_mapped_to_source_system` failed every such
// row and the only shape that loaded at all was the dishonest one.
//
// The set stays CLOSED: a globally valid but unlisted class (e.g.
// 'synthetic-demo') still resolves to no source system and is rejected. The
// modeled fill is replace-before-certified-use under the #1303 ingest lifecycle
// — a connector-derived / csv-admin-upload cost-center and service-catalog pack
// supersedes it through the sanctioned ingest, and it is never re-labeled to
// look certified in the meantime. The SCALAR-ANCHOR role authority below is
// deliberately BYTE-UNTOUCHED: modeled scalar lineage stays inadmissible.
const sharedServicesGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: SHARED_SERVICES_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Shared services governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'shared_services_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved back-office shared-function artifacts carrying total spend, addressable ' +
    'spend, savings rate, recovery factor, and program cost after sanctioned ingest and ' +
    'approval. Modeled artifacts are admitted only when explicitly labeled modeled, and ' +
    'keep that label through disclosure.',
});

const marketingSpendGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: MARKETING_SPEND_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Marketing and demand generation governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'marketing_spend_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description: 'Approved marketing and demand-generation artifacts (agency, media, events, martech) carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval.',
});

function transportCogsSource(
  ref: string,
  label: string,
  evidenceSchemaVersion: string,
  detail: string,
): ApprovedSourceSystemDefinition {
  return Object.freeze({
    sourceSystemRef: ref,
    label,
    sourceClass: 'manual-admin',
    allowedSourceClasses: Object.freeze(['connector-derived', 'manual-admin', 'csv-admin-upload']),
    evidenceSchemaVersion,
    runtimeUse: 'governed_evidence_only',
    description: `Approved ${detail} carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval.`,
  });
}

const networkOptimizationGovernedSnapshotSource = transportCogsSource(
  NETWORK_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Network design governed snapshot',
  'network_optimization_governed_snapshot_v1',
  'lane, route, and facility network-design artifacts',
);
// Standing Law 15 (2026-08-04, migrated here by #4602): 'modeled' admits as its
// own class for the capacity-utilization governed snapshot and KEEPS that class
// end to end, so the server-authored "Modeled" disclosure badge rides the
// answer instead of the row being suppressed. The set stays closed; unlisted
// classes remain rejected. Sibling transport-COGS levers keep the shared
// three-class fence (transportCogsSource) unchanged.
const capacityUtilizationGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: CAPACITY_UTILIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Capacity utilization governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'capacity_utilization_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved backhaul, empty-mile, and load-factor utilization artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval. Modeled artifacts are admitted only when explicitly labeled modeled.',
});
const modeMixShiftGovernedSnapshotSource = transportCogsSource(
  MODE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Mode mix governed snapshot',
  'mode_mix_shift_governed_snapshot_v1',
  'shipment mode, weight, distance, and capacity artifacts',
);
// Standing Law 15 (2026-08-04, migrated here by #4695): 'modeled' admits as
// its own class for the fuel-efficiency governed snapshot and KEEPS that class
// end to end, so the server-authored "Modeled" disclosure badge rides the
// answer instead of the row being suppressed — the exact scoped pattern proven
// for caputil/labarb at 8df28e4c. The set stays closed; unlisted classes
// remain rejected. Sibling transport-COGS levers keep the shared three-class
// fence (transportCogsSource) unchanged.
const fuelEfficiencyGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: FUEL_EFFICIENCY_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Fuel and accessorial governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'fuel_efficiency_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved fuel surcharge, accessorial, and carrier invoice-line artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval. Modeled artifacts are admitted only when explicitly labeled modeled.',
});
// CTO ruling #6779 supersedes the previous modeled-denial pin for outside
// contractors only. Its transport-COGS siblings retain the generic closed
// three-class definition below.
const outsideContractorsGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: OUTSIDE_CONTRACTORS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Outside contractors governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'outside_contractors_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved contingent-labor and outsourced-services spend artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval. Modeled artifacts are admitted only when explicitly labeled modeled.',
});
// Standing Law 15 (2026-08-04, migrated here by #4602): 'modeled' admits as its
// own class for the labor-arbitrage governed snapshot and KEEPS that class end
// to end, so the server-authored "Modeled" disclosure badge rides the answer
// instead of the row being suppressed. The set stays closed; unlisted classes
// remain rejected. Sibling transport-COGS levers keep the shared three-class
// fence (transportCogsSource) unchanged.
const laborArbitrageGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: LABOR_ARBITRAGE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Labor arbitrage governed snapshot',
  sourceClass: 'manual-admin',
  allowedSourceClasses: Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'modeled',
  ]),
  evidenceSchemaVersion: 'labor_arbitrage_governed_snapshot_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved role, location-cost, and shoring-mix artifacts carrying total spend, addressable spend, savings rate, recovery factor, and program cost after sanctioned ingest and approval. Modeled artifacts are admitted only when explicitly labeled modeled.',
});
const revenueMixShiftGovernedSnapshotSource = transportCogsSource(
  REVENUE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Revenue mix shift governed snapshot',
  'revenue_mix_shift_governed_snapshot_v1',
  'customer and lane margin-mix and contribution artifacts',
);
const growthCrossSellGovernedSnapshotSource = transportCogsSource(
  GROWTH_CROSS_SELL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Cross-sell growth governed snapshot',
  'growth_cross_sell_governed_snapshot_v1',
  'customer penetration, attach-rate, and white-space artifacts',
);
const churnReductionGovernedSnapshotSource: ApprovedSourceSystemDefinition =
  Object.freeze({
    sourceSystemRef: CHURN_REDUCTION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    label: 'Churn reduction governed snapshot',
    sourceClass: 'connector-derived',
    allowedSourceClasses: Object.freeze([
      'connector-derived',
      'manual-admin',
      'csv-admin-upload',
      'modeled',
    ]),
    evidenceSchemaVersion: 'churn_reduction_governed_snapshot_v1',
    runtimeUse: 'governed_evidence_only',
    description:
      'Approved customer-retention artifacts carrying churn events, cohort retention, and save-play assumptions after sanctioned ingest and approval. Modeled S1 demo raw packs are admitted only when explicitly labeled modeled.',
  });
const workingCapitalGovernedSnapshotSource = transportCogsSource(
  WORKING_CAPITAL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Working capital governed snapshot',
  'working_capital_governed_snapshot_v1',
  'AR aging, DSO/DPO, and payment-term artifacts',
);
const capexDisciplineGovernedSnapshotSource = transportCogsSource(
  CAPEX_DISCIPLINE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  'Capex discipline governed snapshot',
  'capex_discipline_governed_snapshot_v1',
  'capital-plan, asset-register, and project-return artifacts',
);

// Founder-authorized MODELED gl_account operating-cost decomposition (pre-contract
// policy). Labeled modeled/illustrative at every layer; reconciled to governed
// anchors; ingested through the sanctioned csv-admin-upload lifecycle. Carries the
// 3PL COA template (industry config) that gives the exclusion-receipt contract its
// cross-lever common cut. NOT decision-grade. NOT real-ledger sourced.
const modeledGlOperatingCostSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: MODELED_GL_OPERATING_COST_SOURCE_SYSTEM_REF,
  label: 'Modeled gl_account operating-cost decomposition (illustrative)',
  sourceClass: 'csv-admin-upload',
  allowedSourceClasses: Object.freeze(['csv-admin-upload', 'modeled']),
  evidenceSchemaVersion: 'modeled_gl_operating_cost_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved MODELED/illustrative gl_account decomposition of lever operating cost ' +
    '(3PL chart-of-accounts template), reconciled to approved lever anchors and ingested ' +
    'through the sanctioned admin-upload lifecycle for exclusion-receipt member universes.',
});

const esgEmissionsGovernedSnapshotSource: ApprovedSourceSystemDefinition = Object.freeze({
  sourceSystemRef: ESG_EMISSIONS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  label: 'Freight emissions governed snapshot',
  sourceClass: 'connector-derived',
  allowedSourceClasses: Object.freeze(['connector-derived']),
  allowedContextKinds: Object.freeze([
    'decision_agent:esg_emissions:shipment_tonne_km_snapshot',
    'decision_agent:esg_emissions:approved_emission_factor_table',
    'decision_agent:esg_emissions:emission_reduction_target',
    'decision_agent:esg_emissions:abatement_lever_cost_table',
  ]),
  evidenceSchemaVersion: 'esg_emissions_evidence_prep_bundle_v1',
  runtimeUse: 'governed_evidence_only',
  description:
    'Approved freight-emissions evidence carrying governed tonne-kilometres, GLEC/ISO 14083 factor rows, target paths, and abatement-lever cost links after sanctioned ingest and approval.',
});

// Standing Law 15 (2026-08-04), scoped modeled admission for the F-INVEST
// scenario-investment artifacts (#4602 Q1-FIX PR-B) — the exact pattern #4695
// migrated for the fuel-efficiency governed snapshot above.
//
// 'modeled' admits as its own class here and KEEPS that class end to end, so the
// server-authored "Modeled" disclosure badge rides the answer instead of the row
// being suppressed. It is not cosmetic for this family: the value-case artifact's
// value classes are closed to `forecast` and `assumption`
// (SCENARIO_VALUE_CASE_VALUE_CLASSES has no 'observed' member by design), and a
// requirement may be costed off an `industry_benchmark` rung — so an honest
// scenario-investment evidence set is frequently modeled, and suppressing it would
// withhold the very numbers the intent exists to publish.
//
// The set stays CLOSED: an unlisted class (e.g. industry_reference, extrapolated,
// synthetic-demo) resolves to no source system and is rejected upstream as
// unapproved_source_system. This is a NEW source system for a NEW artifact family —
// the finance P&L source systems (financePnlPublicFilingSource /
// financePnlManualAdminSource) are deliberately BYTE-UNTOUCHED; no existing fence is
// widened, and the anti-fabrication fence is not involved either way.
const scenarioInvestmentGovernedArtifactsSource: ApprovedSourceSystemDefinition =
  Object.freeze({
    sourceSystemRef: SCENARIO_INVESTMENT_GOVERNED_ARTIFACTS_SOURCE_SYSTEM_REF,
    label: 'Scenario investment governed artifacts',
    sourceClass: 'modeled',
    allowedSourceClasses: Object.freeze([
      'modeled',
      'manual-admin',
      'csv-admin-upload',
      'connector-derived',
    ]),
    allowedContextKinds: Object.freeze([
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.requirements,
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.valueCase,
      SCENARIO_INVESTMENT_ARTIFACT_CONTEXT_KINDS.fundingPlan,
    ]),
    evidenceSchemaVersion: SCENARIO_INVESTMENT_REQUIREMENTS_ARTIFACT_SCHEMA_VERSION,
    allowedEvidenceSchemaVersions: Object.freeze([
      SCENARIO_VALUE_CASE_ARTIFACT_SCHEMA_VERSION,
      SCENARIO_FUNDING_PLAN_ARTIFACT_SCHEMA_VERSION,
    ]),
    runtimeUse: 'governed_evidence_only',
    description:
      'Approved scenario investment requirements, value case, and funding plan artifacts — the typed successors to the retired finance investment pseudo-keys — after sanctioned ingest and approval. Modeled artifacts are admitted only when explicitly labeled modeled, and keep that label through disclosure.',
  });

const approvedSourceSystemDefinitions: readonly ApprovedSourceSystemDefinition[] = Object.freeze([
  strategicExecutionCapacityEvidenceSource,
  orgCostOptimizationGovernedSnapshotSource,
  carrierTransportationCostGovernedSnapshotSource,
  portfolioCapacityAllocationGovernedSnapshotSource,
  financePnlPublicFilingSource,
  financePnlManualAdminSource,
  capitalCashBalanceSheetPublicFilingSource,
  industryBenchmarksPublicSource,
  pricingRealizationGovernedSnapshotSource,
  riskClaimsGovernedSnapshotSource,
  costToServeGovernedSnapshotSource,
  activeHeadcountInScopeGovernedSnapshotSource,
  capitalAllocationGovernedSnapshotSource,
  commercialEffectivenessGovernedSnapshotSource,
  pricingDiscountPolicyAuthoritySource,
  pricingDiscountTransactionSnapshotSource,
  productivityLeanAiGovernedSnapshotSource,
  workforceReductionGovernedEvidencePackSource,
  realizationMonitoringGovernedSnapshotSource,
  realizationComponentVarianceSource,
  attributionCounterfactualEvidenceSource,
  attributionDeconflictionReceiptSource,
  allocationLensExclusionReceiptSource,
  allocationLensMemberUniverseSource,
  segmentMarginCleanupGovernedSnapshotSource,
  segmentMarginCleanupMarginFactSource,
  travelExpenseGovernedSnapshotSource,
  indirectProcurementGovernedSnapshotSource,
  professionalFeesGovernedSnapshotSource,
  techSoftwareGovernedSnapshotSource,
  realEstateFootprintGovernedSnapshotSource,
  sharedServicesGovernedSnapshotSource,
  marketingSpendGovernedSnapshotSource,
  networkOptimizationGovernedSnapshotSource,
  capacityUtilizationGovernedSnapshotSource,
  modeMixShiftGovernedSnapshotSource,
  fuelEfficiencyGovernedSnapshotSource,
  outsideContractorsGovernedSnapshotSource,
  laborArbitrageGovernedSnapshotSource,
  revenueMixShiftGovernedSnapshotSource,
  growthCrossSellGovernedSnapshotSource,
  churnReductionGovernedSnapshotSource,
  workingCapitalGovernedSnapshotSource,
  capexDisciplineGovernedSnapshotSource,
  esgEmissionsGovernedSnapshotSource,
  modeledGlOperatingCostSource,
  scenarioInvestmentGovernedArtifactsSource,
]);

const approvedSourceSystemDefinitionByRef = new Map<string, ApprovedSourceSystemDefinition>(
  approvedSourceSystemDefinitions.map((definition) => [definition.sourceSystemRef, definition]),
);

export function listApprovedSourceSystemDefinitions(): readonly ApprovedSourceSystemDefinition[] {
  return approvedSourceSystemDefinitions;
}

export function getApprovedSourceSystemDefinition(
  sourceSystemRef: string,
): ApprovedSourceSystemDefinition {
  const definition = approvedSourceSystemDefinitionByRef.get(sourceSystemRef);

  if (!definition) {
    throw new Error(`Approved source system definition not found: ${sourceSystemRef}`);
  }

  return definition;
}

export function hasApprovedSourceSystemDefinition(sourceSystemRef: string): boolean {
  return approvedSourceSystemDefinitionByRef.has(sourceSystemRef);
}

export function getStrategicExecutionCapacitySourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  // Standing Law 15: 'modeled' is a member of the closed admissible set — the
  // class is carried, never re-labeled, so the "Modeled" disclosure label
  // reaches the answer surface. Unlisted classes still resolve to null.
  if (
    normalized === 'csv-admin-upload' ||
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'modeled'
  ) {
    return STRATEGIC_EXECUTION_CAPACITY_CSV_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getOrgCostOptimizationSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    orgCostOptimizationGovernedSnapshotSource.allowedSourceClasses.includes(
      normalized,
    )
  ) {
    return ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getCarrierTransportationCostSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getFinancePnlSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (normalized === 'public-filing') {
    return FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF;
  }
  if (normalized === 'manual-admin') {
    return FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getIndustryBenchmarkSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (normalized === 'industry_reference' || normalized === 'industry-reference') {
    return INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getPricingRealizationSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getRiskClaimsSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  return riskClaimsGovernedSnapshotSource.allowedSourceClasses.includes(normalized)
    ? riskClaimsGovernedSnapshotSource.sourceSystemRef
    : null;
}

export function getCostToServeSourceSystemRefForSourceClass(sourceClass: string): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return COST_TO_SERVE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getCommercialEffectivenessSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return COMMERCIAL_EFFECTIVENESS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getProductivityLeanAiSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return PRODUCTIVITY_LEAN_AI_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

function sourceSystemRefFromDefinition(
  definition: ApprovedSourceSystemDefinition,
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  return definition.allowedSourceClasses.includes(normalized)
    ? definition.sourceSystemRef
    : null;
}

export function getProfessionalFeesSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(professionalFeesGovernedSnapshotSource, sourceClass);
}

export function getTechSoftwareSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(techSoftwareGovernedSnapshotSource, sourceClass);
}

export function getRealEstateFootprintSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(realEstateFootprintGovernedSnapshotSource, sourceClass);
}

// Driven by the registry definition's own closed set (single source of truth,
// the #4695 pattern), so the resolver cannot drift from the definition it
// resolves. 'modeled' is a member (CTO ruling 2026-08-18, #4602 SHSVC-A) and
// maps to the SAME already-registered governed-snapshot source system — no
// shadow pin and no second registration to keep in step. Unlisted classes still
// resolve to null.
export function getSharedServicesSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(sharedServicesGovernedSnapshotSource, sourceClass);
}

/**
 * ROLE-SCOPED admission authority: the source classes admissible in the
 * shared-services SCALAR-ANCHOR role (#4737 amendment to #4602 SHSVC-FIX).
 *
 * The scalar anchor in `sourceMetadata` is upstream lineage for the snapshot's
 * scalar signals, whose own governance is the artifact row's class. The
 * sanctioned `mixed_source_standard_snapshot` contract (see the client-beta
 * declarative shape at
 * `scripts/evidence-prep/onboard-pack/__fixtures__/shared-services-client-beta.json`)
 * anchors those signals to a company's public filing while the artifact itself
 * arrives as a csv-admin-upload. That exception is LAW here, declared once and
 * imported — it is not a skipped check and not a locally retyped allowlist.
 *
 * The set is the module's three governed row classes PLUS `public-filing`, and
 * it stays closed: a class that is globally valid but unlisted for this role
 * (e.g. `modeled`) is REJECTED, exactly as it is on the artifact row. Widening
 * the role is a reviewed amendment to this constant, never a local override.
 */
export const SHARED_SERVICES_SCALAR_ANCHOR_ADMITTED_SOURCE_CLASSES: readonly string[] =
  Object.freeze([
    'connector-derived',
    'manual-admin',
    'csv-admin-upload',
    'public-filing',
  ]);

/**
 * The scalar-anchor role's admission predicate. Mirrors the row-class getter's
 * normalization so the two authorities cannot disagree on casing or padding.
 */
export function sharedServicesScalarAnchorSourceClassAdmitted(
  sourceClass: string,
): boolean {
  return SHARED_SERVICES_SCALAR_ANCHOR_ADMITTED_SOURCE_CLASSES.includes(
    sourceClass.trim().toLowerCase(),
  );
}

/**
 * SCALAR-role EXECUTION admission (#4751 amendment 3, Codex review 5336762739).
 *
 * The scalar-anchor authority above governed CONTENT PARSING only. Approved
 * formula execution over a snapshot's SCALAR signals binds its evidence to the
 * artifact ROW's identity (the module engine's `buildFormulaEvidence`:
 * artifactId, ingestJobId, sourceClass, sourceSystemRef), and the generic
 * approved-formula runtime admits that evidence from the source system's
 * `allowedSourceClasses`. Adding `modeled` there for the DIMENSIONS role
 * therefore also widened what scalar execution would accept. This authority
 * closes that, at the module's own execution boundary.
 *
 * TRACE FINDING (why this fails closed rather than re-binding): the scalar
 * anchor carries NO governed identity of its own. `sourceMetadata.scalarAnchor`
 * declares reportingPeriod, asOf, sourceClass, sourceRef and sourceArtifactPath
 * — no artifactId, no ingestJobId, and no in-state evidence ref bound to it.
 * Resolving its sourceRef to an approved artifact would require a database
 * lookup, which this seam must not do. The only governed artifact/ingest
 * identity in play is the ROW's, so the rule is: scalar formula execution is
 * admitted only when the identity it binds — the artifact row's — is itself
 * admissible in the SCALAR-ANCHOR role, and the anchor's declared lineage is
 * admissible there too.
 *
 * Governed rows are unaffected: their class is a member of that closed set, so
 * the evidence they bind is byte-identical to before — including a state that
 * declares no scalar anchor at all, which is judged on the row alone. A
 * `modeled` row fails closed under a named reason: its dimensional detail still
 * renders under the dimensions-role admission, but no approved formula executes
 * over scalar signals whose governed lineage is unproven. Nothing is relabeled,
 * no runtime exception is introduced, and no second formula path exists.
 */
export type SharedServicesScalarExecutionDenialReason =
  'scalar_evidence_class_not_admitted_for_scalar_role';

export type SharedServicesScalarExecutionAdmission =
  | Readonly<{ admitted: true }>
  | Readonly<{
      admitted: false;
      denialReason: SharedServicesScalarExecutionDenialReason;
      denialDetail: string;
    }>;

/** The named gap a scalar-lineage denial reports. */
export const SHARED_SERVICES_SCALAR_EVIDENCE_GAP_KEY =
  'shared_services_scalar_governed_lineage';

export function sharedServicesScalarExecutionAdmission(
  input: Readonly<{
    rowSourceClass: string;
    scalarAnchorSourceClass: string | null;
  }>,
): SharedServicesScalarExecutionAdmission {
  // The row's class first: it is the identity formula evidence actually binds.
  // A declared anchor class is checked too, so role-unadmitted scalar lineage is
  // refused at execution and not only at parse. A state that declares no anchor
  // is judged on the row alone — exactly as before this amendment, so every
  // governed row's behaviour is unchanged.
  const roles: ReadonlyArray<readonly [string, string | null]> = [
    ['row', input.rowSourceClass],
    ['scalarAnchor', input.scalarAnchorSourceClass],
  ];
  for (const [role, sourceClass] of roles) {
    if (
      sourceClass !== null &&
      !sharedServicesScalarAnchorSourceClassAdmitted(sourceClass)
    ) {
      return {
        admitted: false,
        denialReason: 'scalar_evidence_class_not_admitted_for_scalar_role',
        denialDetail: `${role}.sourceClass=${sourceClass}`,
      };
    }
  }
  return { admitted: true };
}

/**
 * ROLE-SCOPED admission authority: the source classes admissible in the
 * shared-services DIMENSIONS role (CTO ruling 2026-08-18, #4602 SHSVC-A).
 *
 * The dimensional cost-center and service-catalog rows are governed evidence in
 * their own right, so the role admits exactly the module's admitted ROW classes
 * — bound to the registry definition itself, never a second list that could
 * drift from it. Since the Law-15 amendment above, that set includes `modeled`:
 * honest modeled dimensional detail is scope-admitted, labeled, and badged.
 */
export const SHARED_SERVICES_DIMENSIONS_ADMITTED_SOURCE_CLASSES: readonly string[] =
  sharedServicesGovernedSnapshotSource.allowedSourceClasses;

/**
 * The dimensions role's admission predicate. Mirrors the row-class getter's
 * normalization so the two authorities cannot disagree on casing or padding.
 *
 * `modeled` is admitted ONLY on an artifact row that is itself approved as
 * `modeled`: modeled dimensional detail may be published, but it may never ride
 * a governed-class artifact. That is the #4737 smuggle shape (a
 * `csv-admin-upload` row carrying `modeled` dimensional content) and it stays
 * REJECTED here under `content_source_class_not_admitted_for_module` — this
 * amendment scope-admits the honest shape without re-opening the dishonest one,
 * and relabeling remains impossible in either direction. A row anchor is needed
 * to make that judgement, so an evaluation with no governance record to cohere
 * with (the CSV ingest path) fails closed for `modeled`, exactly as it did
 * before this ruling.
 */
export function sharedServicesDimensionsSourceClassAdmitted(
  sourceClass: string,
  rowSourceClass: string | null,
): boolean {
  const normalized = sourceClass.trim().toLowerCase();
  if (!SHARED_SERVICES_DIMENSIONS_ADMITTED_SOURCE_CLASSES.includes(normalized)) {
    return false;
  }
  if (normalized !== DECISION_AGENT_SOURCE_CLASSES.modeled) return true;
  return (rowSourceClass ?? '').trim().toLowerCase() === normalized;
}

export function getMarketingSpendSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(marketingSpendGovernedSnapshotSource, sourceClass);
}

function transportCogsSourceRefForSourceClass(
  sourceClass: string,
  ref: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload'
  ) {
    return ref;
  }
  return null;
}

export function getNetworkOptimizationSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    NETWORK_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getCapacityUtilizationSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  // Standing Law 15 (#4602): 'modeled' is a member of this module's closed
  // admissible set — the class is carried, never re-labeled, so the "Modeled"
  // disclosure badge reaches the answer surface. Driven by the registry
  // definition's own closed set (single source of truth); unlisted classes
  // still resolve to null. Sibling transport-COGS resolvers are unchanged.
  if (
    capacityUtilizationGovernedSnapshotSource.allowedSourceClasses.includes(
      normalized,
    )
  ) {
    return CAPACITY_UTILIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getModeMixShiftSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    MODE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getFuelEfficiencySourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  // Standing Law 15 (#4695): 'modeled' is a member of this module's closed
  // admissible set — the class is carried, never re-labeled, so the "Modeled"
  // disclosure badge reaches the answer surface. Driven by the registry
  // definition's own closed set (single source of truth); unlisted classes
  // still resolve to null. Sibling transport-COGS resolvers are unchanged.
  if (
    fuelEfficiencyGovernedSnapshotSource.allowedSourceClasses.includes(
      normalized,
    )
  ) {
    return FUEL_EFFICIENCY_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

// Standing Law 15 (#4602 Q1-FIX PR-B, mirroring #4695): driven by the registry
// definition's own closed set (single source of truth), so the admissible classes
// cannot drift from the definition. 'modeled' is a member and is carried, never
// re-labeled; unlisted classes still resolve to null.
export function getScenarioInvestmentArtifactSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    scenarioInvestmentGovernedArtifactsSource.allowedSourceClasses.includes(
      normalized,
    )
  ) {
    return SCENARIO_INVESTMENT_GOVERNED_ARTIFACTS_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getOutsideContractorsSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(outsideContractorsGovernedSnapshotSource, sourceClass);
}

export function getLaborArbitrageSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  // Standing Law 15 (#4602): 'modeled' is a member of this module's closed
  // admissible set — the class is carried, never re-labeled, so the "Modeled"
  // disclosure badge reaches the answer surface. Driven by the registry
  // definition's own closed set (single source of truth); unlisted classes
  // still resolve to null. Sibling transport-COGS resolvers are unchanged.
  if (
    laborArbitrageGovernedSnapshotSource.allowedSourceClasses.includes(
      normalized,
    )
  ) {
    return LABOR_ARBITRAGE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getRevenueMixShiftSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    REVENUE_MIX_SHIFT_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getGrowthCrossSellSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    GROWTH_CROSS_SELL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getChurnReductionSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return CHURN_REDUCTION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

export function getWorkingCapitalSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    WORKING_CAPITAL_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getCapexDisciplineSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return transportCogsSourceRefForSourceClass(
    sourceClass,
    CAPEX_DISCIPLINE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  );
}

export function getIndirectProcurementSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(indirectProcurementGovernedSnapshotSource, sourceClass);
}

export function getTravelExpenseSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  return sourceSystemRefFromDefinition(travelExpenseGovernedSnapshotSource, sourceClass);
}

export function getSegmentMarginCleanupSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  // Standing Law 15: 'modeled' is a member of the closed admissible set — the
  // class is carried, never re-labeled, so the "Modeled" disclosure label
  // reaches the answer surface. Unlisted classes still resolve to null.
  if (
    normalized === 'connector-derived' ||
    normalized === 'manual-admin' ||
    normalized === 'csv-admin-upload' ||
    normalized === 'modeled'
  ) {
    return SEGMENT_MARGIN_CLEANUP_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF;
  }
  return null;
}

/**
 * #2863 margin-fact evidence class resolver (NEW; the governed-snapshot resolver
 * above is unchanged). 'modeled' ONLY: the module loader fails closed with this
 * mapping — if 'modeled' is ever revoked here, every margin-fact artifact stops
 * loading and the module returns to its named-gap posture. The future WSM
 * order-line connector-derived path requires its own reviewed change here.
 */
export function getSegmentMarginCleanupMarginFactSourceSystemRefForSourceClass(
  sourceClass: string,
): string | null {
  const normalized = sourceClass.trim().toLowerCase();
  if (normalized === 'modeled') {
    return SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF;
  }
  return null;
}
